// ✅ src/pages/Asset/AssetTransferReportListing.jsx  (FULL FILE - UPDATED WITH localStorage + commonHeaders)

import React, { useEffect, useMemo, useState, useContext } from "react";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEye,
  FaChevronDown,
  FaChevronUp,
  FaPrint,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";

const API_BASE = (import.meta?.env?.VITE_API_BASE || "https://nissi-india.com").replace(
  /\/$/,
  ""
);

// -------- Helpers --------
const normSelect = (v) => {
  if (v === undefined || v === null) return "";
  const s = String(v).trim();
  if (!s || s.toLowerCase() === "select") return "";
  return s;
};

const mapBranchOptions = (rows) => {
  if (!Array.isArray(rows)) return [];
  return rows.map((r, idx) => {
    const keys = Object.keys(r || {});
    const idKey =
      keys.find((k) => /fld_branchid|branchid|_branchid|^id$/i.test(k)) ||
      keys[0];
    const nameKey =
      keys.find((k) => /fld_branchname|branchname|name|BranchName/i.test(k)) ||
      keys[1] ||
      keys[0];

    return {
      value: String(r?.[idKey] ?? ""),
      label: String(r?.[nameKey] ?? r?.[idKey] ?? `Branch ${idx + 1}`),
      raw: r,
    };
  });
};

const guessTransferId = (row) => {
  const candidates = [
    "TransferID",
    "transferId",
    "transferID",
    "fld_transferid",
    "fld_transferID",
    "fld_TransferId",
    "fld_transid",
    "TranID",
    "tranid",
    "id",
  ];
  for (const k of candidates) {
    if (
      row?.[k] !== undefined &&
      row?.[k] !== null &&
      String(row[k]).trim() !== ""
    ) {
      return String(row[k]);
    }
  }
  return "";
};

const parseDDMMYYYY = (s) => {
  const str = String(s || "").trim();
  const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  return { dd: m[1], mm: m[2], yyyy: m[3] };
};

const AssetTransferReportListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // ✅ localStorage data
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("branchid") ||
    "";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // ✅ numeric fallback if backend needs number
  const numericUserId = Number(userId) || 0;
  const numericBranchId = Number(branchId) || 0;

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // optional: mode same as ASPX QueryString:
  // mode=1 => from fixed ; mode=2 => to fixed
  const mode = "";

  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    fromDate: "03/09/2025",
    toDate: "10/09/2025",
    fromLocation: "",
    toLocation: "",
  });

  const [rights, setRights] = useState({
    hasRights: true,
    hasPrintRights: true,
  });

  const [branches, setBranches] = useState([]);
  const [uiLock, setUiLock] = useState({
    fromBranchLocked: false,
    toBranchLocked: false,
  });

  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingView, setLoadingView] = useState(false);

  const [records, setRecords] = useState([]);

  const [paging, setPaging] = useState({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 1,
  });

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const loadInit = async () => {
    if (!numericUserId) {
      toast.error("UserId missing in localStorage (userId/loginuserid)");
      return;
    }

    setLoadingInit(true);
    try {
      const url =
        `${API_BASE}/api/transferDetails/init?userId=${numericUserId}` +
        `&branchtype=${encodeURIComponent(branchType)}` +
        `&branchid=${numericBranchId}` +
        `&finYearId=${encodeURIComponent(finYearId)}` +
        `&finYear=${encodeURIComponent(finYear)}` +
        (mode ? `&mode=${encodeURIComponent(mode)}` : "");

      const res = await fetch(url, {
        method: "GET",
        headers: commonHeaders,
      });

      const data = await res.json();

      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to load init");
        return;
      }

      setRights({
        hasRights: !!data?.rights?.hasRights,
        hasPrintRights: !!data?.rights?.hasPrintRights,
      });

      const mapped = mapBranchOptions(data?.dropdowns?.branches || []);
      setBranches(mapped);

      const ui = data?.ui || {};
      setUiLock({
        fromBranchLocked: !!ui.fromBranchLocked,
        toBranchLocked: !!ui.toBranchLocked,
      });

      setFilters((prev) => ({
        ...prev,
        fromLocation: ui.defaultFromBranchId
          ? String(ui.defaultFromBranchId)
          : prev.fromLocation,
        toLocation: ui.defaultToBranchId
          ? String(ui.defaultToBranchId)
          : prev.toLocation,
      }));
    } catch (err) {
      toast.error(err?.message || "Init error");
    } finally {
      setLoadingInit(false);
    }
  };

  const loadView = async (page = 1) => {
    if (!parseDDMMYYYY(filters.fromDate) || !parseDDMMYYYY(filters.toDate)) {
      toast.error("Date format must be dd/MM/yyyy");
      return;
    }

    setLoadingView(true);
    try {
      const payload = {
        fromDate: filters.fromDate,
        toDate: filters.toDate,
        fromLocation: normSelect(filters.fromLocation),
        toLocation: normSelect(filters.toLocation),
        page,
        pageSize: paging.pageSize,
        finYearId,
        finYear,
        branchId,
        branchtype: branchType,
        branchType,
        userId,
        loginuserid: userId,
      };

      const res = await fetch(`${API_BASE}/api/transferDetails/view`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...commonHeaders,
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data?.success) {
        toast.error(data?.message || "Failed to load listing");
        return;
      }

      setRecords(Array.isArray(data?.data) ? data.data : []);
      setPaging({
        page: Number(data?.paging?.page || page),
        pageSize: Number(data?.paging?.pageSize || paging.pageSize),
        totalRecords: Number(data?.paging?.totalRecords || 0),
        totalPages: Number(data?.paging?.totalPages || 1),
      });

      toast.success("Listing loaded");
    } catch (err) {
      toast.error(err?.message || "View error");
    } finally {
      setLoadingView(false);
    }
  };

  const handleViewClick = () => {
    setPaging((p) => ({ ...p, page: 1 }));
    loadView(1);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > paging.totalPages) return;
    loadView(nextPage);
  };

  const renderPageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 10;
    const total = paging.totalPages || 1;

    if (total <= maxVisible) {
      for (let i = 1; i <= total; i++) pages.push(i);
      return pages;
    }

    const cur = paging.page;
    let start = Math.max(1, cur - 4);
    let end = Math.min(total, start + 9);
    start = Math.max(1, end - 9);

    if (start > 1) pages.push(1, "...");
    for (let i = start; i <= end; i++) pages.push(i);
    if (end < total) pages.push("...", total);
    return pages;
  }, [paging.page, paging.totalPages]);

  const handlePrintRow = (row) => {
    const transferId = guessTransferId(row);
    if (!transferId) {
      toast.error("Transfer ID not found in this row. Tell me the column name from API.");
      return;
    }

    window.open(
      `${API_BASE}/api/transferDetails/print/${transferId}?finYearId=${encodeURIComponent(
        finYearId
      )}&finYear=${encodeURIComponent(finYear)}&branchId=${encodeURIComponent(
        branchId
      )}&branchType=${encodeURIComponent(branchType)}&userId=${encodeURIComponent(
        userId
      )}`,
      "_blank"
    );
  };

  const columns = useMemo(() => {
    if (!records.length) {
      return [
        { key: "TransferDate", label: "Transfer Date" },
        { key: "AssetType", label: "Asset Type" },
        { key: "AssetCode", label: "Asset ID/Code" },
        { key: "FromLocation", label: "From Location" },
        { key: "ToLocation", label: "To Location" },
        { key: "Status", label: "Status" },
      ];
    }

    const keys = Object.keys(records[0] || {});
    const preferred = [
      "fld_transferdate",
      "TransferDate",
      "transferDate",
      "fld_assettype",
      "AssetType",
      "fld_assetcode",
      "AssetCode",
      "fld_fromlocation",
      "FromLocation",
      "fld_tolocation",
      "ToLocation",
      "fld_status",
      "Status",
    ];

    const chosen = [];
    for (const k of preferred) {
      if (keys.includes(k) && !chosen.includes(k)) chosen.push(k);
    }
    const finalKeys = chosen.length >= 6 ? chosen.slice(0, 6) : keys.slice(0, 6);

    return finalKeys.map((k) => ({ key: k, label: k }));
  }, [records]);

  useEffect(() => {
    loadInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rights.hasRights === false) {
      toast.error("You dont have rights to access this page");
    }
  }, [rights.hasRights]);

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET TRANSFER REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Asset Transfer Report Listing
            </h1>

            <div className="text-sm opacity-80">
              {paging.totalRecords ? `Total: ${paging.totalRecords}` : ""}
            </div>
          </div>

          {/* Filters */}
          <div
            className={`mb-6 rounded-lg border ${
              isDark ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-gray-50"
            }`}
          >
            <div
              className={`flex justify-between items-center p-4 cursor-pointer ${
                isDark ? "hover:bg-gray-700/50" : "hover:bg-gray-100"
              }`}
              onClick={() => setShowFilters(!showFilters)}
            >
              <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Search Filters
              </h3>
              {showFilters ? <FaChevronUp /> : <FaChevronDown />}
            </div>

            {showFilters && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      From Date
                    </label>
                    <input
                      type="text"
                      value={filters.fromDate}
                      onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                      placeholder="dd/mm/yyyy"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      To Date
                    </label>
                    <input
                      type="text"
                      value={filters.toDate}
                      onChange={(e) => handleFilterChange("toDate", e.target.value)}
                      placeholder="dd/mm/yyyy"
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      From Location
                    </label>
                    <select
                      value={filters.fromLocation}
                      onChange={(e) => handleFilterChange("fromLocation", e.target.value)}
                      disabled={loadingInit || uiLock.fromBranchLocked}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {branches.map((b) => (
                        <option key={b.value} value={b.value}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      To Location
                    </label>
                    <select
                      value={filters.toLocation}
                      onChange={(e) => handleFilterChange("toLocation", e.target.value)}
                      disabled={loadingInit || uiLock.toBranchLocked}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="">Select</option>
                      {branches.map((b) => (
                        <option key={b.value} value={b.value}>
                          {b.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex justify-center">
                  <button
                    onClick={handleViewClick}
                    disabled={loadingView}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${
                      loadingView ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaEye />
                    {loadingView ? "Loading..." : "View"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            {records.length === 0 ? (
              <div className={`p-12 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-xl font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  No Records Found
                </div>
                <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  No asset transfer records match the current filter criteria.
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-4 text-center font-semibold border-r border-white/20">
                        S.No
                      </th>
                      {columns.map((c) => (
                        <th
                          key={c.key}
                          className="px-4 py-4 text-center font-semibold border-r border-white/20"
                        >
                          {c.label}
                        </th>
                      ))}
                      <th className="px-4 py-4 text-center font-semibold">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {records.map((row, idx) => (
                      <tr
                        key={idx}
                        className={`transition-colors duration-200 ${
                          idx % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-center">
                          {(paging.page - 1) * paging.pageSize + idx + 1}
                        </td>
                        {columns.map((c) => (
                          <td key={c.key} className="px-4 py-3 text-center">
                            {row?.[c.key] ?? ""}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center">
                          {rights.hasPrintRights !== false && (
                            <button
                              onClick={() => handlePrintRow(row)}
                              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold shadow hover:shadow-md transition-all ${
                                isDark
                                  ? "bg-blue-700 hover:bg-blue-600 text-white"
                                  : "bg-blue-600 hover:bg-blue-700 text-white"
                              }`}
                              title="Print"
                            >
                              <FaPrint />
                              Print
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Pagination */}
          {paging.totalPages > 1 && (
            <div className="flex justify-start mt-6 items-center gap-2">
              <button
                onClick={() => handlePageChange(paging.page - 1)}
                disabled={paging.page <= 1}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "bg-gray-800 text-gray-200 border-gray-700 disabled:opacity-40"
                    : "bg-white text-gray-700 border-gray-300 disabled:opacity-40"
                }`}
              >
                <FaChevronLeft />
              </button>

              <div className="flex gap-1 flex-wrap">
                {renderPageNumbers.map((p, idx) => (
                  <button
                    key={`${p}-${idx}`}
                    onClick={() => p !== "..." && handlePageChange(p)}
                    disabled={p === "..."}
                    className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                      p === paging.page
                        ? "bg-blue-600 text-white border-blue-600"
                        : p === "..."
                        ? isDark
                          ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                          : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                        : isDark
                        ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>

              <button
                onClick={() => handlePageChange(paging.page + 1)}
                disabled={paging.page >= paging.totalPages}
                className={`px-3 py-2 rounded border ${
                  isDark
                    ? "bg-gray-800 text-gray-200 border-gray-700 disabled:opacity-40"
                    : "bg-white text-gray-700 border-gray-300 disabled:opacity-40"
                }`}
              >
                <FaChevronRight />
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetTransferReportListing;